<script setup lang="ts">
import SearchSelect from "../../ui/form/SearchSelect.vue";
import TextInput from "../../ui/form/TextInput.vue";
import CheckInput from "../../ui/form/CheckInput.vue";
import TextAreaInput from "../../ui/form/TextAreaInput.vue";
import NumberInput from "../../ui/form/NumberInput.vue";
import { ref, onMounted } from "vue";
import ModalFormTemplate from "../../../components/ui/Templates/ModalFormTemplate.vue";
import { useFormValidation } from "../../../composables/useFormValidation";
import { required } from "../../../utils/validation";
import {
  Field,
  ReportDesign,
  Table,
  aggregateFunctionOptions,
  fieldTypeOptions,
  alignOptions,
} from "../../../types/report";
import {  mapSqlTypeToVueType } from "../../../types/mapSqlTypeToVueType";

const props = defineProps<{
  report?: ReportDesign;
  title: string;
  data?: any;
  table?: Table;
  field?: Field;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  pivot?: boolean;
}>();

const emit = defineEmits<{
  (e: "addField", data: any): void;
  (e: "close"): void;
}>();

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  resetForm,
  setFormData,
} = useFormValidation({
    id: props.data?.id || "",
    tableId: props.data?.tableId || "",
    tableName: props.data?.tableName || "",
    tableAlias: props.data?.tableAlias || "",
    name: props.data?.name || "",
    alias: props.data?.alias || "",
    caption: props.data?.caption || "",
    visible: props.data?.visible || true,
    aggregateFunction: props.data?.aggregateFunction || "",
    align: props.data?.align || "Left",
    type: mapSqlTypeToVueType(props.data?.type || ""),
    width: props.data?.width || 100,
    decimals: props.data?.decimals || 0,
    sort_order: props.data?.sort_order || 0,
    raw: props.data?.raw || false,
    rawQuery: props.data?.rawQuery || "",
    manual: props.data?.manual || false,
    sqlQuery: props.data?.sqlQuery ?? '',
    ignoreGroup: props.data?.ignoreGroup || false,
});

setFieldRules("id", [required("Table is required")]);
setFieldRules("tableId", !fields.value.raw.value ? [required("Table Name is required")] : []);
setFieldRules("name", !fields.value.raw.value ? [required("Field is required")]: []);
setFieldRules("alias", [required("Field Alias is required")]);
setFieldRules("caption", [required("Field Caption is required")]);
setFieldRules("align", [required("Alignment is required")]);
setFieldRules("type", [required("Field Type is required")]);


const handleSubmit = async () => {
  setFieldRules("name", !fields.value.raw.value ? [required("Field is required")]: []);
  setFieldRules("tableId", !fields.value.raw.value ? [required("Table Name is required")]: []);
  console.log(fields.value)
  if (validateForm()) {
    console.log("Form Data:", formData.value);
    emit("addField", formData.value);
  }
};

const setField = () => {
    if (props.field) {
        fields.value.id.value = "field_" + Math.random().toString(36).substring(2, 15);
        fields.value.tableId.value = props.field.tableId || "";
        fields.value.tableName.value = props.field.tableName,
        fields.value.tableAlias.value = props.field.tableAlias,
        fields.value.name.value = props.field.name || "";
        fields.value.alias.value = props.field.alias || "";
        fields.value.caption.value = props.field.caption || "";
        fields.value.visible.value = props.field.visible || true;
        fields.value.aggregateFunction.value = props.field.aggregateFunction || "";
        fields.value.align.value = props.field.align || "Left";
        fields.value.type.value = props.field.type || "";
        fields.value.width.value = props.field?.width || 100;
        fields.value.decimals.value = props.field?.decimals || 0;
        fields.value.sort_order.value = props.field?.sort_order || props.report?.fields.length || 0;
        fields.value.raw.value = props.field?.raw || false;
        fields.value.rawQuery.value = props.field?.rawQuery || "";
        fields.value.sqlQuery.value = props.field?.sqlQuery || "";
    }
};

const isReadOnly = ref(true);
onMounted(() => {
    console.log('field to add', props.field)
  isReadOnly.value = props.mode == "view";
  if (props.mode == "create") {
    resetForm();
    setField()
  } else if (props.data) {
    setFormData(props.data);
  }
});
</script>
<template>
  <ModalFormTemplate
    :data="data"
    :mode="mode"
    :title="title"
    endPoint=""
    :perm="[]"
    :tabList="[]"
    :formData="formData"
    :ignore-permission="true"
    @save="handleSubmit"
    @close="$emit('close')"
  >
    <template #form>
      <div class="grid md:grid-cols-4 gap-6">        
        <TextInput
          v-model="fields.tableName.value"
          label="Table Name"
          class="col-span-2"
          v-if="!fields.raw.value"
          :readonly="true"
          :error="fields.tableName.touched ? fields.tableName.error : null"
          @blur="fields.tableName.touched = true"
        />
         <TextInput
          v-model="fields.tableAlias.value"
          label="Table Alias"
          class="col-span-2"
          v-if="!fields.raw.value"
          :readonly="true"
          :error="fields.tableAlias.touched ? fields.tableAlias.error : null"
          @blur="fields.tableAlias.touched = true"
        />
        <TextInput
          v-model="fields.name.value"
          label="Field Name"
          class="col-span-1"
          v-if="!fields.raw.value"
          :readonly="true"
          :error="fields.name.touched ? fields.name.error : null"
          @blur="fields.name.touched = true"
        />
        <TextInput
          v-model="fields.alias.value"
          label="Field Alias"
          class="col-span-1"
          :readonly="isReadOnly"
          :error="fields.alias.touched ? fields.alias.error : null"
          @blur="fields.alias.touched = true"
        />
        <TextInput
          v-model="fields.caption.value"
          label="Field Caption"
          class="col-span-2"
          :readonly="isReadOnly"
          :error="fields.caption.touched ? fields.caption.error : null"
          @blur="fields.caption.touched = true"
        />

        <div class="col-span-4" v-if="fields.raw.value && !props.pivot">
          <TextAreaInput
            v-model="fields.rawQuery.value"
            label="SQL Query"
            class="w-full"
            v-if="fields.raw.value"
            :readonly="isReadOnly"
            :error="fields.rawQuery.touched ? fields.rawQuery.error : null"
            @blur="fields.rawQuery.touched = true" />
        </div>
        
        <div class="col-span-4 md:grid md:grid-cols-9 gap-4">
          <SearchSelect
            label="Field Type"
            v-model="fields.type.value"
            class="col-span-2"
            :error="
              fields.type.touched ? fields.type.error : null
            "
            @blur="fields.type.touched = true"
            :options="fieldTypeOptions"
          />
        <CheckInput
          v-model="fields.visible.value"
          label="Visible"
          class="col-span-1"
          :readonly="isReadOnly"
          :error="fields.visible.touched ? fields.visible.error : null"
          @blur="fields.visible.touched = true" />
        
        <SearchSelect
            label="Alignment"
            v-model="fields.align.value"
            class="col-span-2"
            :error="
              fields.align.touched ? fields.align.error : null
            "
            @blur="fields.align.touched = true"
            :options="alignOptions"
          />

          <NumberInput
            v-model="fields.sort_order.value"
            label="Sort Order"
            class="col-span-2"
            :readonly="isReadOnly"
            :error="fields.sort_order.touched ? fields.sort_order.error : null"
            @blur="fields.sort_order.touched = true"
          />

        <NumberInput
            v-model="fields.width.value"
            label="Width"
            class="col-span-2"
            :readonly="isReadOnly"
            :error="fields.width.touched ? fields.width.error : null"
            @blur="fields.width.touched = true"
          />
          </div>

          <NumberInput
            v-model="fields.decimals.value"
            label="Decimals"
            v-if="fields.type.value == 'number' && field?.name.toLocaleLowerCase() !== 'id' && !field?.name.toLocaleLowerCase().includes('_id')"
            class="col-span-1"
            :readonly="isReadOnly"
            :error="fields.decimals.touched ? fields.decimals.error : null"
            @blur="fields.decimals.touched = true"
          />
        <SearchSelect
            label="Aggregate Function"
            v-if="fields.type.value == 'number' && field?.name.toLocaleLowerCase() !== 'id' && !field?.name.toLocaleLowerCase().includes('_id') && !props.pivot"
            v-model="fields.aggregateFunction.value"
            :error="
              fields.aggregateFunction.touched ? fields.aggregateFunction.error : null
            "
            @blur="fields.aggregateFunction.touched = true"
            :options="aggregateFunctionOptions"
          />
          <TextAreaInput
            v-model="fields.sqlQuery.value"
            v-if="!fields.raw.value"
            label="SQL Query"
            class="w-full col-span-2"
            :readonly="isReadOnly"
            :error="fields.sqlQuery.touched ? fields.sqlQuery.error : null"
            @blur="fields.sqlQuery.touched = true" />
            <CheckInput
            v-model="fields.ignoreGroup.value"
            label="Ignore Grouping"
            class="col-span-1"
            :readonly="isReadOnly"
            :error="fields.ignoreGroup.touched ? fields.ignoreGroup.error : null"
            @blur="fields.ignoreGroup.touched = true" />
      </div>      
    </template>
  </ModalFormTemplate>
</template>
